import type { ChatMessage, ClientCard, Source } from "../types";
import { TypingDots } from "./TypingDots";
import { Sources } from "./Sources";
import { Card } from "./Card";
import { RepCard } from "./RepCard";

interface Props {
  message: ChatMessage;
  cards: ClientCard[];
}

export function Message({ message, cards }: Props) {
  // While the assistant message is streaming and hasn't received its first
  // text delta, show the typing indicator inside the bubble.
  const showTyping =
    message.role === "assistant" && message.isStreaming && !message.content;

  const sources = dedupeByUrl(message.sources ?? []);

  // Inline [N] and [[card:...]] markers are stripped from the rendered text.
  // The source chips below carry citations; the card blocks carry catalog
  // recommendations. Both stream in mid-response, so a partial marker
  // (e.g., "[[card") may briefly appear and then disappear once the closing
  // brackets arrive — acceptable flicker.
  // Prefer the deterministic catalog set the API attached to this turn (the
  // full, complete result of a structured plan query). Fall back to resolving
  // [[card:...]] markers from the model's text for ordinary recommendations.
  const matchedCards =
    message.role !== "assistant"
      ? []
      : message.cards && message.cards.length > 0
        ? message.cards
        : resolveCardMarkers(message.content, cards);
  const renderedText =
    message.role === "assistant"
      ? stripMarkers(message.content)
      : message.content;

  return (
    <div class={`message ${message.role}`}>
      <div class="bubble">{showTyping ? <TypingDots /> : renderedText}</div>
      {message.role === "assistant" && matchedCards.length > 0 && (
        <div class="card-list">
          {matchedCards.map((c) => (
            <Card key={`${c.type}:${c.slug}`} card={c} />
          ))}
        </div>
      )}
      {message.role === "assistant" && (message.reps?.length ?? 0) > 0 && (
        <div class="rep-list">
          {message.reps!.map((r, i) => (
            <RepCard key={`${r.name}-${i}`} rep={r} />
          ))}
        </div>
      )}
      {message.role === "assistant" && sources.length > 0 && (
        <Sources sources={sources} />
      )}
    </div>
  );
}

// Retrieval often returns several chunks from the same source URL. Dedupe
// by URL so the source list shows one entry per page.
function dedupeByUrl(sources: Source[]): Source[] {
  const seen = new Set<string>();
  const result: Source[] = [];
  for (const s of sources) {
    if (!seen.has(s.url)) {
      seen.add(s.url);
      result.push(s);
    }
  }
  return result;
}

const CARD_MARKER_RE = /\[\[card:([a-z0-9_-]+):([a-z0-9_-]+)\]\]/gi;
const CARD_LIMIT = 3;

function resolveCardMarkers(
  text: string,
  cards: ClientCard[],
): ClientCard[] {
  if (cards.length === 0) return [];
  // Index once per call. Small (Ward has ~62; bigger clients still small).
  const index = new Map<string, ClientCard>();
  for (const c of cards) {
    index.set(`${c.type}:${c.slug}`, c);
  }
  const seen = new Set<string>();
  const out: ClientCard[] = [];
  CARD_MARKER_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = CARD_MARKER_RE.exec(text)) !== null) {
    const key = `${m[1]!.toLowerCase()}:${m[2]!}`;
    if (seen.has(key)) continue;
    const card = index.get(key);
    if (!card) continue;
    seen.add(key);
    out.push(card);
    if (out.length >= CARD_LIMIT) break;
  }
  return out;
}

function stripMarkers(text: string): string {
  // Strip [N] citation markers, [[card:type:slug]] markers, and any
  // markdown formatting the model leaks despite the no-markdown system
  // rule. The widget renders text literally, so a stray "**Plan Name**"
  // would otherwise reach the visitor as visible asterisks. The
  // prompt-side rule still does the heavy lifting; this is a safety
  // net against model variance. Strip-order matters: markers first
  // (they're more specific), then markdown emphasis, then whitespace
  // tidy-up.
  return text
    .replace(CARD_MARKER_RE, "")
    .replace(/\s*\[\d+\]/g, "")
    .replace(/\*\*([^*\n]+)\*\*/g, "$1")
    .replace(/__([^_\n]+)__/g, "$1")
    .replace(/(^|[\s(])\*([^*\n]+?)\*(?=[\s).,;:!?]|$)/g, "$1$2")
    .replace(/(^|[\s(])_([^_\n]+?)_(?=[\s).,;:!?]|$)/g, "$1$2")
    .replace(/`([^`\n]+)`/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\[([^\]]+)\]\(https?:\/\/[^)]+\)/g, "$1")
    .replace(/\s+([.,;:!?])/g, "$1")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
